'use client';

import { useRef, useState, useEffect } from 'react';
import { colors, spacing } from '~/utils/tailwindUtils';
import { Icon, IconName } from '../Icon';

type CheckboxOption = {
  key: string;
  value: string;
  label: string;
};

export type CheckboxOptions = CheckboxOption[];

export interface CheckboxProps {
  checkboxOptions: CheckboxOptions;
  onChange: (e: any) => void;
  onBlur?: (e: any) => void;
  value: (string | number)[];
}

const Checkbox = ({ checkboxOptions = [], onChange, value }: CheckboxProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const onSetValue = (value: (string | number)[]) => {
    onChange(value);
  };

  // Check if content is scrollable and update scroll indicator visibility
  useEffect(() => {
    const checkScrollable = () => {
      if (containerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = containerRef.current;
        // Show indicator if there's more content below (within 5px threshold)
        const hasMoreContent = scrollHeight > clientHeight + 5;
        const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - 5;
        setShowScrollIndicator(hasMoreContent && !isScrolledToBottom);
      }
    };

    checkScrollable();
    // Recheck when options change
    const timeoutId = setTimeout(checkScrollable, 100);

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollable);
    }

    return () => {
      clearTimeout(timeoutId);
      if (container) {
        container.removeEventListener('scroll', checkScrollable);
      }
    };
  }, [checkboxOptions]);

  const handleScrollDown = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        top: 500,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="space-y-2 relative">
      <div
        ref={containerRef}
        className="flex flex-col space-y-2 max-h-[60vh] overflow-y-auto no-scrollbar pr-2"
      >
        {checkboxOptions.map((option) => (
          <label key={option.key} className="flex items-center space-x-2 cursor-pointer">
            <div className="relative">
              {/* Custom Checkbox */}
              <input
                type="checkbox"
                id={option.key}
                value={option.value}
                checked={value.includes(option.value)}
                onChange={() =>
                  onSetValue(
                    value.includes(option.value)
                      ? value.filter((v) => v !== option.value)
                      : [...value, option.value],
                  )
                }
                className="h-5 w-5 appearance-none border border-gray-400 rounded cursor-pointer checked:bg-red-500 checked:border-red-500 peer"
              />
              <Icon
                name={IconName.Check}
                className="absolute left-1/2 top-1/2 h-3 w-3 text-white transform -translate-x-1/2 -translate-y-2 opacity-0 peer-checked:opacity-100"
                color={colors.white}
                customStrokeWidth={3}
              />
            </div>
            <span className="text-sm text-gray-700 pb-1">{option.label}</span>
          </label>
        ))}
      </div>
      {/* Scroll indicator - semi-transparent chevron down icon */}
      {showScrollIndicator && (
        <div className="absolute bottom-0 left-0 -right-2 flex justify-center h-9 pb-1 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none">
          <button
            type="button"
            onClick={handleScrollDown}
            className="pointer-events-auto opacity-50 hover:opacity-75 transition-opacity cursor-pointer"
            aria-label="Scroll down to see more options"
          >
            <Icon name={IconName.ChevronDown} size={spacing[6]} color={colors.gray[600]} />
          </button>
        </div>
      )}
    </div>
  );
};

export { Checkbox };
